import React from 'react';

import clsx from 'clsx';

import { Paper, Table, TableBody, TableContainer, TableHead } from '@material-ui/core';

import { StyledTableCell, StyledTableRow } from 'components/StyledComponents';
import InputSearchWithMultiselect from 'components/InputSearchWithMultiselect/InputSearchWithMultiselect';
import SelectInput from 'components/SelectInput/SelectInput';

import { getLocationWithSublocationAndAssetName, isTeamMemberUser } from 'helpers/AppHelpers';
import { getSublocations, enrichLocationOptions } from '../../utils/locations';

import styles from './LooseItemsTable.module.scss';

import { FormServerAssetSearch } from 'components/FormComponents';
import { useFormContext } from 'react-hook-form';

const AssembledStatus = 'Assembled';
const DisassembledStatus = 'Disassembled';

const LooseItemsTable = ({
  tableState,
  setTableState,
  locationList,
  values,
  setValues,
  isFirstTableView,
  isStatus,
  userRole,
  isOwnAssembly
}) => {
  const { setValue } = useFormContext();

  const isFirstNameCell = (index) => {
    return !(index !== 0 && tableState[index]?.id === tableState[index - 1]?.id);
  };

  const getCellNumber = (rowId) => {
    return tableState.filter(({ id }) => id === rowId)?.length;
  };

  const onSelect = (name, value, index) => {
    setValue(`rows[${index}]`, null);
    const newLocation = { ...value };
    const newArray = [...tableState];
    newArray[index][name] = newLocation;
    if (name === 'location') {
      newArray[index].sublocation = null;
      newArray[index].containedInAsset = null;
    }
    setTableState(newArray);
    if (values.active) setValues({ active: false, isDisabled: false });
  };

  const onSublocationSelect = (item, index) => {
    const newArray = [...tableState];
    newArray[index] = { ...newArray[index], sublocation: item.value, containedInAsset: null };
    setTableState(newArray);

    setValue(`rows[${index}]`, null);
  };

  const selectAsset = (name, containedInAsset, index) => {
    setValue(name, containedInAsset);

    if (!containedInAsset?.id) return;
    const { currentLocationId, currentSublocationId } = containedInAsset;
    const location = locationList?.find(({ id }) => id === currentLocationId) || {};
    const sublocation =
      location.sublocations?.find(({ id }) => id === currentSublocationId) || null;
    if (!location?.id) return;
    const newArray = [...tableState];
    newArray[index] = { ...newArray[index], location, sublocation, containedInAsset };
    setTableState(newArray);
  };

  return (
    <TableContainer className={styles.tableContainer} component={Paper}>
      <Table>
        <TableHead>
          <StyledTableRow>
            <StyledTableCell>Loose item</StyledTableCell>
            {isFirstTableView() ? (
              <>
                <StyledTableCell>Required quantity</StyledTableCell>
                <StyledTableCell>Point of origin (Sublocation) [Asset]</StyledTableCell>
                <StyledTableCell>Selected quantity</StyledTableCell>
                <StyledTableCell>
                  From BAA<span className={styles.footnote}>[1]</span>
                </StyledTableCell>
              </>
            ) : (
              <>
                <StyledTableCell>
                  Released quantity(BAA<span className={styles.footnote}>[1]</span>)
                </StyledTableCell>
                <StyledTableCell>Point of origin (Sublocation) [Asset]</StyledTableCell>
                <StyledTableCell>Point of destination</StyledTableCell>
                <StyledTableCell>Sublocation</StyledTableCell>
                <StyledTableCell>Contained In</StyledTableCell>
              </>
            )}
          </StyledTableRow>
        </TableHead>
        <TableBody>
          {tableState.map((row, index) => (
            <React.Fragment key={row.id}>
              {isFirstTableView() ? (
                <StyledTableRow>
                  {isFirstNameCell(index) && (
                    <>
                      <StyledTableCell rowSpan={getCellNumber(row.id)} className={styles.nameCell}>
                        {row?.name || ''}
                      </StyledTableCell>
                      <StyledTableCell
                        rowSpan={getCellNumber(row.id)}
                        className={styles.quantityCell}>
                        {row?.requiredQuantity || 0}
                      </StyledTableCell>
                    </>
                  )}
                  <StyledTableCell className={styles.originCell}>
                    {isStatus(AssembledStatus) ? (
                      <>{row?.pointOfOrigin || ''}</>
                    ) : (
                      <>{getLocationWithSublocationAndAssetName(row)}</>
                    )}
                  </StyledTableCell>
                  <StyledTableCell className={styles.selectedQtyCell}>
                    {row?.selectedQuantity || 0}
                  </StyledTableCell>
                  <StyledTableCell className={styles.selectedBaaCell}>
                    {row?.selectedByAheadAccount || 0}
                  </StyledTableCell>
                </StyledTableRow>
              ) : (
                <StyledTableRow>
                  <StyledTableCell className={styles.nameCell}>{row?.name || ''}</StyledTableCell>
                  <StyledTableCell className={styles.quantityCell}>
                    {row?.requiredQuantity || 0}
                    {row?.requiredBaaQuantity ? '(' + row.requiredBaaQuantity + ')' : ''}
                  </StyledTableCell>
                  <StyledTableCell className={styles.originCell}>
                    {row?.pointOfOrigin || ''}
                  </StyledTableCell>
                  <StyledTableCell
                    className={clsx(
                      styles.locationCell,
                      isStatus(DisassembledStatus) && styles.noSpace
                    )}>
                    {isStatus(DisassembledStatus) &&
                    !isTeamMemberUser(userRole) &&
                    isOwnAssembly() ? (
                      <InputSearchWithMultiselect
                        name="location"
                        defaultValue={row?.location?.id || ''}
                        options={enrichLocationOptions(locationList, row.location) || []}
                        onFilterSelect={onSelect}
                        index={index}
                        multiselect={false}
                        tableView={true}
                      />
                    ) : (
                      <>{row?.location?.siteCode || ''}</>
                    )}
                  </StyledTableCell>
                  <StyledTableCell
                    className={clsx(
                      styles.sublocationCell,
                      isStatus(DisassembledStatus) && styles.noSpace
                    )}>
                    {isStatus(DisassembledStatus) &&
                    !isTeamMemberUser(userRole) &&
                    isOwnAssembly() ? (
                      <SelectInput
                        name="sublocation"
                        value={row?.sublocation?.id || ''}
                        menuItems={getSublocations(locationList, row?.location?.id) || []}
                        onSelect={onSublocationSelect}
                        index={index}
                        tableView
                        clearable
                      />
                    ) : (
                      <>{row?.sublocation?.name || ''}</>
                    )}
                  </StyledTableCell>
                  <StyledTableCell
                    className={clsx(
                      styles.containedInCell,
                      isStatus(DisassembledStatus) && styles.noSpace
                    )}>
                    {isStatus(DisassembledStatus) ? (
                      <FormServerAssetSearch
                        name={`rows[${index}]`}
                        max={100}
                        onSelect={(n, v) => selectAsset(n, v, index)}
                        getLabel={(option) => option?.drCode || ''}
                        getQuery={(searchQuery) => ({
                          pagination: { limit: 39, page: 1 },
                          filters: { isDeleted: false, prefixTypes: [2, 3, 4, 5], searchQuery }
                        })}
                        className={styles.containedInField}
                      />
                    ) : (
                      <>{row?.containedInAsset?.drCode || ''}</>
                    )}
                  </StyledTableCell>
                </StyledTableRow>
              )}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default LooseItemsTable;
